// Shared alert formatting helpers — the single source of truth for how an alert
// is titled, scored and time-stamped across the Alert Feed (s12) and Alert
// Detail (s13). Kept framework-free (pure functions) so both screens render an
// alert identically without duplicating logic.
import type { Alert } from '../hooks/useAlerts'

export type Sev = 'critical' | 'high' | 'medium' | 'info'

/** Compact "3m ago / 4h ago / 2d ago" relative time. */
export function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

/** Absolute timestamp in the viewer's local timezone (stored UTC). */
export function localTimestamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

/** Numeric price drop percentage (0–100), or null if the alert has no prices. */
export function dropPct(alert: Alert): number | null {
  if (!alert.old_value || !alert.new_value) return null
  const oldV = parseFloat(alert.old_value)
  const newV = parseFloat(alert.new_value)
  if (!isFinite(oldV) || !isFinite(newV) || oldV === 0) return null
  return Math.round(((oldV - newV) / oldV) * 100)
}

export function getSev(alert: Alert): Sev {
  if (alert.alert_type === 'price_drop') {
    const pct = dropPct(alert)
    if (pct != null) {
      if (pct >= 20) return 'critical'
      if (pct >= 10) return 'high'
    }
    return 'high'
  }
  if (alert.alert_type === 'new_promo') return 'high'
  if (alert.alert_type === 'new_sku') return 'medium'
  return 'info'
}

export function getSevLabel(sev: Sev): string {
  return { critical: 'CRITICAL', high: 'HIGH', medium: 'MEDIUM', info: 'INFO' }[sev]
}

/** Severity → CannaSpy palette CSS var (coral alert accent per BRAND.md). */
export function sevColor(sev: Sev): string {
  if (sev === 'critical' || sev === 'high') return 'var(--danger)'
  if (sev === 'medium') return 'var(--accent)'
  return 'var(--text-3)'
}

/** Human title for an alert, mirroring the s12 feed row. */
export function getTitle(alert: Alert): string {
  const name = alert.competitor_name
  if (alert.alert_type === 'price_drop') {
    const pct = dropPct(alert)
    if (pct != null) return `${name} dropped pricing ${pct}%`
    return `${name} dropped prices`
  }
  if (alert.alert_type === 'new_promo') return `${name} launched a new promotion`
  if (alert.alert_type === 'new_sku') return `${name} added new products`
  if (alert.alert_type === 'price_increase') return `${name} raised prices`
  return `${name} — ${alert.alert_type.replace(/_/g, ' ')}`
}

/** One-line subtitle describing the change. */
export function getSub(alert: Alert): string {
  if (alert.old_value && alert.new_value && alert.alert_type === 'price_drop')
    return `$${alert.old_value} → $${alert.new_value}`
  if (alert.alert_type === 'new_promo') return 'New deal detected on their menu'
  if (alert.alert_type === 'new_sku') return 'Menu update detected'
  return alert.alert_type.replace(/_/g, ' ')
}

/** Short badge label for the alert type (hero breadcrumb). */
export function getTypeBadge(alert: Alert): string {
  return {
    price_drop: 'Price Drop',
    price_increase: 'Price Increase',
    new_promo: 'New Promo',
    new_sku: 'Catalog Change',
    sku_removed: 'Catalog Change',
    block_cancelled: 'Block Activity',
    competitor_blocked: 'Block Activity',
  }[alert.alert_type] ?? alert.alert_type.replace(/_/g, ' ')
}
